import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, model, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, MbsSize } from 'mbs-ui-kit';
import { buildsThatSupportCommand } from 'mbs-ui-shared@components/download-build/download-build.constants';
import { BrandsFacade } from 'mbs-ui-shared@facades/brands.facade';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import { BuildType, PermissionsEnum } from 'mbs-ui-shared@models';
import { Build, BuildOsType, BuildVersionType, ConnectFromBuild, getSupportCompanyMinimalVersion } from 'mbs-ui-shared@models/build';
import { BuildDownloadOptions, BuildDownloadService } from 'mbs-ui-shared@services/build-download.service';
import { ConnectSettingsService } from 'mbs-ui-shared@services/connect.settings.service';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { getNormalizedVersion } from 'mbs-ui-shared@utils/get-normalized-version';
import { EMPTY, Observable } from 'rxjs';
import { filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { CompanyWizardFacade } from 'wizards/services/entity-wizards/company/company-wizard.facade';
import {
  CompanyDownloadIcons,
  CompanyDownloadProduct,
  CompanyDownloadStepDefaultValue,
  CompanyDownloadStepValue
} from '../../models/company-wizard-models';

export enum ConnectAppLink {
  Windows = 'windows',
  Mac = 'mac'
}

type DownloadPlatformRow = { appLink?: ConnectAppLink; buildType?: BuildType; label: string };

type DownloadPlatform = {
  icon: string;
  name: string;
  rows: DownloadPlatformRow[];
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, I18NextPipe, NgbTooltipModule],
  selector: 'mbs-company-download-step',
  standalone: true,
  templateUrl: './company-download-step.component.html'
})
export class CompanyDownloadStepComponent {
  private readonly ability = inject(AbilityService);
  private readonly brandsFacade = inject(BrandsFacade);
  private readonly buildDownloadService = inject(BuildDownloadService);
  private readonly buildsFacade = inject(BuildsFacade);
  private readonly clipboard = inject(Clipboard);
  private readonly companyWizardFacade = inject(CompanyWizardFacade);
  private readonly connectSettings = inject(ConnectSettingsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly environment = inject(EnvironmentService);
  private readonly i18nPipe = inject(I18NextPipe);

  public readonly value = model<CompanyDownloadStepValue>(CompanyDownloadStepDefaultValue);

  public readonly companyDownloadProduct = CompanyDownloadProduct;
  public readonly buildVersionType = BuildVersionType;
  public readonly mbsSize = MbsSize;

  public readonly products: { label: string; product: CompanyDownloadProduct }[] = [
    { label: this.getText('backup'), product: CompanyDownloadProduct.Backup },
    { label: this.getText('rmm'), product: CompanyDownloadProduct.Rmm },
    { label: this.getText('connect'), product: CompanyDownloadProduct.Connect }
  ];

  public readonly channels: { label: string; type: BuildVersionType }[] = [
    { label: this.getText('public'), type: BuildVersionType.Public },
    { label: this.getText('sandbox'), type: BuildVersionType.Sandbox },
    { label: this.getText('beta'), type: BuildVersionType.Beta }
  ];

  private readonly backupPlatforms: DownloadPlatform[] = [
    {
      icon: CompanyDownloadIcons.windows,
      name: this.getText('windows'),
      rows: [
        { buildType: BuildType.CommonWindowsBuild, label: this.getText('download') },
        { buildType: BuildType.VMware, label: this.getText('vmware') }
      ]
    },
    {
      icon: `${CompanyDownloadIcons.mac} text-lg`,
      name: this.getText('mac'),
      rows: [
        { buildType: BuildType.MacArm, label: this.getText('appleSilicon') },
        { buildType: BuildType.LinuxMac, label: this.getText('intel') }
      ]
    },
    {
      icon: `${CompanyDownloadIcons.linux} text-lg`,
      name: this.getText('linux'),
      rows: [
        { buildType: BuildType.LinuxRedHat6, label: this.getText('rpm') },
        { buildType: BuildType.LinuxUbuntu14, label: this.getText('deb') }
      ]
    }
  ];

  private readonly rmmPlatforms: DownloadPlatform[] = [
    {
      icon: CompanyDownloadIcons.windows,
      name: this.getText('windows'),
      rows: [
        { buildType: BuildType.RMMAgentExe, label: this.getText('exe') },
        { buildType: BuildType.RMMAgentMsi, label: this.getText('msi') }
      ]
    },
    {
      icon: `${CompanyDownloadIcons.mac} text-lg`,
      name: this.getText('mac'),
      rows: [{ buildType: BuildType.RMMAgentMac, label: this.getText('intel') }]
    },
    {
      icon: `${CompanyDownloadIcons.linux} text-lg`,
      name: this.getText('linux'),
      rows: [
        { buildType: BuildType.RMMAgentLinuxRpm, label: this.getText('rpm') },
        { buildType: BuildType.RMMAgentLinuxDeb, label: this.getText('deb') }
      ]
    }
  ];

  private readonly connectPlatforms: DownloadPlatform[] = [
    {
      icon: CompanyDownloadIcons.windows,
      name: this.getText('windows'),
      rows: [
        { buildType: BuildType.CBLRA, label: this.getText('connectAgent') },
        { appLink: ConnectAppLink.Windows, label: this.getText('connectApplication') }
      ]
    },
    {
      icon: `${CompanyDownloadIcons.mac} text-lg`,
      name: this.getText('mac'),
      rows: [
        { buildType: BuildType.MacRA, label: this.getText('connectAgent') },
        { appLink: ConnectAppLink.Mac, label: this.getText('connectApplication') }
      ]
    }
  ];

  private readonly builds: Signal<Build[]> = toSignal(this.buildsFacade.builds$, { initialValue: [] });
  private readonly buildsSettings = toSignal(this.buildsFacade.buildsSettings$, { initialValue: null });

  private readonly connectFromBuilds = toSignal(this.buildsFacade.connectFromBuilds$, { initialValue: [] });
  private readonly connectClientUrl = toSignal(this.connectSettings.getConnectClientBuild(), {
    initialValue: this.environment.raDownloadURL.windows
  });
  private readonly requestedBuildType = signal<BuildType>(null);

  public readonly buildsInProgress = toSignal(this.buildsFacade.buildsInProgress$, { initialValue: [] });
  public readonly licensedBrandList = toSignal(this.brandsFacade.getActive$(), { initialValue: [] });

  public readonly companyId = computed(() => this.companyWizardFacade.company()?.id);
  public readonly isSandboxEnabled = computed(() => !!this.buildsSettings()?.isSandBoxAvailable);
  public readonly isConnect = computed(() => this.value().product === CompanyDownloadProduct.Connect);
  public readonly isRmm = computed(() => this.value().product === CompanyDownloadProduct.Rmm);

  public readonly platforms: Signal<DownloadPlatform[]> = computed(() => {
    if (this.isConnect()) return this.connectPlatforms;

    return this.isRmm() ? this.rmmPlatforms : this.backupPlatforms;
  });

  // Build types never overlap between products, so the platform list itself is the filter
  public readonly buildsByType = computed(() => {
    const types = new Set(this.platforms().flatMap(({ rows }) => rows.map(({ buildType }) => buildType)));

    return (this.builds() ?? [])
      .filter(({ mbsBuildType }) => types.has(mbsBuildType))
      .reduce<Partial<Record<BuildType, Build>>>((acc, build) => ({ ...acc, [build.mbsBuildType]: build }), {});
  });

  public readonly visiblePlatforms: Signal<DownloadPlatform[]> = computed(() =>
    this.platforms().filter((platform) => this.visibleRows(platform).length)
  );

  public visibleRows(platform: DownloadPlatform): DownloadPlatform['rows'] {
    return platform.rows.filter((row) => {
      if (row.appLink) return !!this.appLinkUrl(row.appLink);

      const build = this.buildsByType()[row.buildType];

      return !!build && (!!this.availableChannels(build).length || this.canGenerate(build));
    });
  }

  public appLinkUrl(appLink: ConnectAppLink): string {
    if (appLink === ConnectAppLink.Mac) return this.environment.raDownloadURL.mac;

    return this.connectFromWindowsBuild()?.downloadLink || this.connectClientUrl();
  }

  public appLinkLabel(appLink: ConnectAppLink): string {
    const version = appLink === ConnectAppLink.Windows ? this.connectFromWindowsBuild()?.version : null;

    return version ? `${this.getText('public')} v${version}` : this.getText('download');
  }

  public appLinkClickHandler(appLink: ConnectAppLink): void {
    window.open(this.appLinkUrl(appLink), '_blank');
  }

  public copyAppLinkClickHandler(appLink: ConnectAppLink): void {
    this.clipboard.copy(this.appLinkUrl(appLink));
  }

  public copyLinkClickHandler(build: Build, channel: BuildVersionType): void {
    this.buildDownloadService.copyLink(build, channel, this.downloadOptions()).subscribe();
  }

  public canGenerate(build: Build): boolean {
    if (!build || build.public || !this.ability.can('read', PermissionsEnum.Rebranding)) return false;

    return !this.isSandboxEnabled() || !build.sandbox;
  }

  public isBuildInProgress(build: Build): boolean {
    return this.requestedBuildType() === build.mbsBuildType || !!this.buildsInProgress()?.includes(build.mbsBuildType);
  }

  public isAnyBuildInProgress(): boolean {
    return this.requestedBuildType() !== null || !!this.buildsInProgress()?.length;
  }

  public generateClickHandler(build: Build): void {
    this.requestedBuildType.set(build.mbsBuildType);

    this.buildDownloadService
      .generateBuild(build)
      .pipe(
        switchMap((res) => (res?.success ? this.whenBuildGenerated(build) : EMPTY)),
        finalize(() => this.requestedBuildType.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public productChangeHandler(product: CompanyDownloadProduct): void {
    this.value.set({ ...this.value(), product });
  }

  public isChannelAvailable(build: Build, channel: BuildVersionType): boolean {
    if (channel === BuildVersionType.Sandbox && !this.isSandboxEnabled()) return false;

    return !!build?.[channel]?.downloadLink;
  }

  public availableChannels(build: Build): { label: string; type: BuildVersionType }[] {
    return this.channels.filter((channel) => this.isChannelAvailable(build, channel.type));
  }

  public channelLabel(build: Build, channel: { label: string; type: BuildVersionType }): string {
    const version = build?.[channel.type]?.version;

    return version ? `${channel.label} v${version}` : channel.label;
  }

  // Older agents ignore the companyId the link carries, so the computer would silently land in the wrong company
  public isUnsupportedByCompany(build: Build, channel: BuildVersionType): boolean {
    if (!this.companyId() || channel === BuildVersionType.Beta) return false;

    return getNormalizedVersion(build?.[channel]?.version) < getSupportCompanyMinimalVersion(build.mbsBuildType);
  }

  public isCommandSupported(build: Build): boolean {
    return buildsThatSupportCommand.includes(build.mbsBuildType);
  }

  public downloadClickHandler(build: Build, channel: BuildVersionType): void {
    this.buildDownloadService.download(build, channel, this.downloadOptions()).subscribe();
  }

  public copyCommandClickHandler(build: Build, channel: BuildVersionType): void {
    this.buildDownloadService.copyCommand(build, channel, this.downloadOptions()).subscribe();
  }

  // Private section
  private connectFromWindowsBuild(): ConnectFromBuild {
    return this.connectFromBuilds()?.find(({ buildOs }) => buildOs === BuildOsType.Windows);
  }

  private whenBuildGenerated({ mbsBuildType }: Build): Observable<Build> {
    return this.buildsFacade.builds$.pipe(
      map((builds) => builds?.find((item) => item.mbsBuildType === mbsBuildType)),
      filter((updated) => !this.canGenerate(updated)),
      take(1)
    );
  }

  private downloadOptions(): BuildDownloadOptions {
    return {
      companyId: this.companyId(),
      isSandboxEnabled: this.isSandboxEnabled(),
      licensedBrandList: this.licensedBrandList()
    };
  }

  private getText(key: string): string {
    return this.i18nPipe.transform(`wizards:entityWizards.company.downloadStep.${key}`);
  }
}
